import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CharacterSummary } from '../components/ikigai/CharacterSummary';
import { ProfileTimeline } from '../components/ikigai/ProfileTimeline';
import { useYouReadModel } from '../hooks/useYouReadModel';
import { recordSlice04Marker } from '../lib/you/instrumentation';

function Section({
  title,
  testId,
  children,
}: {
  title: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <section
      data-testid={testId}
      className="rounded-lg border border-neutral-200 bg-white/75 p-4 shadow-sm"
    >
      <h2 className="text-base font-semibold text-neutral-900">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function EmptyState() {
  return (
    <div data-testid="you-empty-state" className="rounded-lg border border-dashed border-neutral-200 bg-white/70 p-5 text-center">
      <p className="font-serif text-lg text-neutral-800">Your profile is still forming.</p>
      <p className="mt-2 text-sm text-neutral-500">
        Connect sources or act on Today missions when you are ready. This page will not run analysis on its own.
      </p>
      <div className="mt-4 flex flex-wrap justify-center gap-3 text-sm">
        <Link className="font-medium text-emerald-700 hover:text-emerald-800" to="/connections">Connections</Link>
        <Link className="font-medium text-neutral-600 hover:text-neutral-800" to="/">Today</Link>
      </div>
    </div>
  );
}

export function You() {
  const location = useLocation();
  const {
    isLoading,
    profileLoaded,
    observations,
    characterSummary,
    timeline,
    ikigaiNodes,
    demographicLabels,
  } = useYouReadModel();

  const hasAnyContent =
    observations.length > 0 ||
    Boolean(characterSummary) ||
    timeline.length > 0 ||
    ikigaiNodes.length > 0 ||
    demographicLabels.length > 0;

  useEffect(() => {
    recordSlice04Marker({
      kind: 'route_render',
      status: 'success',
      route: location.pathname,
      counts: {
        observations: observations.length,
        summary: characterSummary ? 1 : 0,
        timeline: timeline.length,
        ikigaiNodes: ikigaiNodes.length,
        demographics: demographicLabels.length,
      },
      flags: {
        profileLoaded,
        readOnly: true,
      },
      reasonCode: 'you_readonly_surface',
    });
  }, [
    location.pathname,
    observations.length,
    characterSummary,
    timeline.length,
    ikigaiNodes.length,
    demographicLabels.length,
    profileLoaded,
  ]);

  return (
    <div
      data-testid="you-readonly-surface"
      data-resolved-surface="you"
      className="mx-auto flex w-full max-w-3xl flex-col gap-4 pb-10"
    >
      <header className="rounded-lg border border-neutral-200 bg-white/80 p-5 shadow-sm">
        <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">Read-only profile</p>
        <h1 className="mt-1 font-serif text-3xl font-semibold text-neutral-950">You</h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-neutral-600">
          A local read model of what OwnYou currently understands. Agents update these records; this surface only reads them.
        </p>
      </header>

      {isLoading ? (
        <div data-testid="you-loading-state" className="rounded-lg border border-neutral-200 bg-white/70 p-5 text-sm text-neutral-500">
          Loading profile read model...
        </div>
      ) : !hasAnyContent ? (
        <EmptyState />
      ) : (
        <>
          <Section title="Profile observations" testId="you-profile-section">
            <div
              data-testid="you-profile-observations"
              data-count={observations.length}
              className="grid gap-2 sm:grid-cols-2"
            >
              {observations.length === 0 ? (
                <p className="text-sm text-neutral-500">No profile observations are stored yet.</p>
              ) : observations.map(observation => (
                <div key={observation.id} className="rounded-lg border border-neutral-100 bg-neutral-50 p-3">
                  <p className="text-sm font-medium text-neutral-900">{observation.label}</p>
                  <p className="mt-1 text-xs text-neutral-500">{observation.detail}</p>
                  {typeof observation.confidence === 'number' && (
                    <p className="mt-2 text-[11px] text-neutral-400">{observation.confidence}% confidence</p>
                  )}
                </div>
              ))}
            </div>
          </Section>

          <Section title="Character summary" testId="you-character-section">
            <div
              data-testid="you-character-summary"
              data-present={characterSummary ? 'true' : 'false'}
              data-category-count={characterSummary?.categories.length ?? 0}
            >
              {characterSummary ? (
                <CharacterSummary profile={characterSummary} />
              ) : (
                <p className="text-sm text-neutral-500">No cached character summary yet.</p>
              )}
            </div>
          </Section>

          <Section title="Profile timeline" testId="you-timeline-section">
            <div
              data-testid="you-profile-timeline"
              data-count={timeline.length}
              data-order={timeline.map(event => event.type).join(',')}
            >
              {timeline.length > 0 ? (
                <ProfileTimeline events={timeline} />
              ) : (
                <p className="text-sm text-neutral-500">No profile timeline events yet.</p>
              )}
            </div>
          </Section>

          <Section title="Ikigai interpretation" testId="you-ikigai-section">
            <div
              data-testid="you-ikigai-readonly"
              data-node-count={ikigaiNodes.length}
              className="grid gap-2 sm:grid-cols-2"
            >
              {ikigaiNodes.length === 0 ? (
                <p className="text-sm text-neutral-500">No Ikigai interpretation is stored yet.</p>
              ) : ikigaiNodes.map(node => (
                <div key={node.id} data-testid={`you-ikigai-node-${node.kind}`} className="rounded-lg border border-neutral-100 bg-neutral-50 p-3">
                  <p className="text-sm font-medium text-neutral-900">{node.label}</p>
                  <p className="mt-1 text-xs capitalize text-neutral-500">{node.kind.replace('-', ' ')}</p>
                  {typeof node.confidence === 'number' && (
                    <p className="mt-2 text-[11px] text-neutral-400">{node.confidence}% confidence</p>
                  )}
                </div>
              ))}
            </div>
          </Section>

          <Section title="Profile attributes" testId="you-demographics-section">
            <div
              data-testid="you-demographics"
              data-count={demographicLabels.length}
              className="flex flex-wrap gap-2"
            >
              {demographicLabels.length === 0 ? (
                <p className="text-sm text-neutral-500">No profile attributes are stored yet.</p>
              ) : demographicLabels.map(label => (
                <span
                  key={label.id}
                  data-testid="you-demographic-chip"
                  className="rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1 text-xs text-neutral-700"
                >
                  {label.label}: {label.value}
                </span>
              ))}
            </div>
          </Section>
        </>
      )}
    </div>
  );
}

export default You;
